/**
 * Scroll Position context. Components wrapped in ScrollProvider have
 * access to smoothed scroll `position` and `progress` values.
 * The `position` value respresents actual pixel scroll distance, while
 * the `progress` value respresents relative scroll position within the page.
 */

import type { SpringConfig } from '@react-spring/core'
import { Controller } from '@react-spring/core'
import clamp from 'lodash/clamp'
import { createContext, useCallback, useEffect, useMemo, useRef } from 'react'
import type { FunctionComponent, MutableRefObject } from 'react'
import { useInView } from 'react-intersection-observer'

export interface ScrollProviderProps {
    config?: SpringConfig
    includeScrollIn?: boolean
    includeScrollOut?: boolean
    getHeaderHeight?: () => number
}

type onUpdateRefType = () => void

interface ScrollProviderValue {
    controller: Controller
    onUpdateRef?: MutableRefObject<onUpdateRefType>
}

const defaultValue = {
    controller: new Controller({ position: 0, progress: 0 }),
}

// Provider
export const ScrollProvider: FunctionComponent<ScrollProviderProps> = ({
    config,
    includeScrollIn = false,
    includeScrollOut = false,
    getHeaderHeight,
    ...rest
}) => {
    const onUpdateRef = useRef<onUpdateRefType>(
        null
    ) as MutableRefObject<onUpdateRefType>
    const scrollController = useMemo(
        () =>
            new Controller({
                position: 0,
                progress: 0,
                config,
                onChange: () => onUpdateRef.current?.(),
            }),
        [config]
    )
    const [inViewRef, inView] = useInView()
    const scrollRef = useRef<HTMLDivElement | null>(null)

    const handleScroll = useCallback(() => {
        const headerHeight = getHeaderHeight?.() ?? 0
        if (scrollRef.current && document.scrollingElement) {
            const { top, height } = scrollRef.current.getBoundingClientRect()
            const relativeTop = top + document.scrollingElement.scrollTop
            const viewportScroll = includeScrollIn
                ? Math.min(relativeTop, window.innerHeight)
                : headerHeight
            const totalScroll =
                viewportScroll +
                height -
                (includeScrollOut ? 0 : window.innerHeight - headerHeight)

            const position = viewportScroll - top
            const progress = clamp(
                totalScroll === 0 ? 0 : position / totalScroll,
                0,
                1
            )

            // only update spring if the scroll position has changed since last update
            const {
                springs: {
                    position: { goal: positionGoal },
                },
            } = scrollController

            if (positionGoal !== position) {
                scrollController.start({ position, progress })
            }
        }
    }, [scrollController])

    const setRefs = useCallback(
        (element: any) => {
            inViewRef(element)
            scrollRef.current = element
        },
        [inViewRef]
    )

    // apply event listeners
    useEffect(() => {
        if (inView) {
            handleScroll()
            window.addEventListener('scroll', handleScroll)
        }
        return () => window.removeEventListener('scroll', handleScroll)
    }, [handleScroll, inView])

    return (
        <div ref={setRefs}>
            <ScrollContext.Provider
                value={{ controller: scrollController, onUpdateRef }}
                {...rest}
            />
        </div>
    )
}

// Context
export const ScrollContext = createContext<ScrollProviderValue>(defaultValue)
